import axios from 'axios'
import awsconfig from '@/aws-exports'
import cookieUrl from '@/const/set-cookie-url.js'
import Cookies from 'js-cookie'
import { generateNonce } from '@/utils/base.js'

// --- Reusable CSRF-cookie bridge helpers ------------------------------------
const CSRF_COOKIE_NAME = 'csrf_nonce'
const CSRF_COOKIE_DOMAIN = '.alc.co.jp'
const CSRF_TTL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Calls the Lambda endpoint that issues secure HttpOnly cookies containing the
 * Cognito id token. The token is transmitted via headers only.
 * A nonce stored in a non-HttpOnly cookie (double-submit) is forwarded in the
 * `x-csrf-lambda` header to mitigate CSRF.
 *
 * @param {Object} params
 * @param {string} params.idToken - Cognito id_token (JWT)
 */
export async function setSecureCookie({ idToken }) {

    const endpoint = cookieUrl.set_cookie_url(awsconfig.aws_user_pools_id)
    const csrfNonce = Cookies.get('csrf_nonce') || ''

    try {
        await axios.post(
            endpoint,
            null, // empty body – payload moves to headers
            {
                timeout: 5000,
                withCredentials: true,
                headers: {
                    'x-id-token': idToken,
                    'x-csrf-lambda': csrfNonce,
                },
            },
        )
    } catch (error) {
        console.error('[CookieService] /set-cookie call failed', error)
        throw error
    }
}

/**
 * High-level helper that wraps the full double-submit flow expected by the
 * /set-cookie Lambda: it mints/sets a nonce cookie, calls the low-level
 * setSecureCookie() that actually hits the endpoint, then deletes the nonce on
 * success.  If the network request fails the nonce is intentionally left in
 * place so that an immediate retry can reuse it (it will still expire after
 * CSRF_TTL_MS).
 *
 * @param {Object} params
 * @param {string} params.idToken     Cognito id_token
 */
export async function issueShopAuthCookies({ idToken }) {
    // 1.  Generate + persist nonce with short TTL
    const nonce = generateNonce()
    const expiry = new Date(Date.now() + CSRF_TTL_MS)
    Cookies.set(CSRF_COOKIE_NAME, nonce, {
        sameSite: 'Lax',
        secure: true,
        domain: CSRF_COOKIE_DOMAIN,
        expires: expiry,
    })

    // 2.  Invoke the existing low-level sender
    try {
        await setSecureCookie({ idToken })
        // 3.  Cleanup on success
        Cookies.remove(CSRF_COOKIE_NAME, { domain: CSRF_COOKIE_DOMAIN, path: '/' })
    } catch (err) {
        // Leave the nonce cookie for potential retry and re-throw so callers
        // can decide what to do.
        throw err
    }
}

// ---------------------------------------------------------------------------
// Low-level helper that contacts the ClearCognitoCookie endpoint to signal a
// logout. No tokens are required – only the CSRF nonce.
// ---------------------------------------------------------------------------

async function clearSecureCookie () {
    const endpoint = cookieUrl.clear_cookie_url(awsconfig.aws_user_pools_id)
    const csrfNonce = Cookies.get(CSRF_COOKIE_NAME) || ''

    await axios.post(
        endpoint,
        null,
        {
            timeout: 5000,
            withCredentials: true,
            headers: {
                'x-csrf-lambda': csrfNonce,
            },
        },
    )
}

// ---------------------------------------------------------------------------
// High-level wrapper mirroring issueShopAuthCookies: handles nonce lifecycle
// so callers do not need to think about CSRF details.
// ---------------------------------------------------------------------------

export async function revokeShopAuthCookies () {
    // 1.  Ensure a fresh nonce
    const nonce = generateNonce()
    const expiry = new Date(Date.now() + CSRF_TTL_MS)
    Cookies.set(CSRF_COOKIE_NAME, nonce, {
        sameSite: 'Lax',
        secure: true,
        domain: CSRF_COOKIE_DOMAIN,
        expires: expiry,
    })

    try {
        await clearSecureCookie()
    } finally {
        // Remove the nonce regardless of success to keep state clean
        Cookies.remove(CSRF_COOKIE_NAME, { domain: CSRF_COOKIE_DOMAIN, path: '/' })
    }
} 